import axios from 'axios'

import env from '~config/env'

const maiaApi = axios.create({
  baseURL: env.MAIA_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

const getMaiaTokens = () => ({
  accessToken: localStorage.getItem('maia_access_token'),
  maiaToken: localStorage.getItem('maia_token'),
})

maiaApi.interceptors.request.use((config) => {
  const { accessToken, maiaToken } = getMaiaTokens()

  config.headers = {
    ...config.headers,
  }

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  if (maiaToken) {
    config.headers['X-Maia-Token'] = maiaToken
  }

  return config
})

const getConversations = () => {
  return maiaApi
    .get<
      Array<{
        id: string
        title: string
        user_id: number
        incognito: boolean
        personality?: 'neutral' | 'encouraging' | 'reassuring'
        created_at: string
      }>
    >('/api/v1/chats/')
    .then((response) => {
      return response.data.map((chat) => ({
        id: chat.id,
        subject: chat.title || 'Sans titre',
        unreadCount: 0,
        isActive: true,
        participants: [],
        latestMessage: undefined,
        incognito: chat.incognito,
        personality: chat.personality,
      })) as ThreadTeaser[]
    })
}

const createNewConversation = () => {
  return maiaApi
    .post<{
      id: string
      title: string
      user_id: number
      incognito: boolean
      created_at: string
    }>('/api/v1/chats/', {
      title: 'Nouvelle discussion',
    })
    .then((response) => {
      return {
        id: response.data.id,
        subject: response.data.title,
        isActive: true,
        updatedAt: response.data.created_at,
        createdAt: response.data.created_at,
      } as ResponseNewConversationRequest
    })
}

const getChatMessages = (chatId: string | number) => {
  return maiaApi
    .get<
      Array<{
        id: string
        rank: number
        role: string
        content: string
        status: string
        is_tool_call: boolean
        created_at: string
        error_message?: string
        error_reported: boolean
        feedback?: string
      }>
    >(`/api/v1/chats/${chatId}/messages/`)
    .then((response) => {
      return response.data
        .filter(
          (msg) =>
            !msg.is_tool_call &&
            (msg.role === 'user' || msg.role === 'assistant')
        )
        .map((msg) => ({
          id: msg.id,
          body: msg.content,
          user: {
            id:
              msg.role === 'user'
                ? Number(localStorage.getItem('userId') || '0')
                : 0,
            fullName: msg.role === 'user' ? 'Vous' : 'Mentor IA',
            role: msg.role === 'assistant' ? ['chatbot'] : undefined,
          } as UserTeaser,
          createdAt: msg.created_at,
        })) as Message[]
    })
}

const sendMessageFeedback = (
  chatId: string | number,
  messageId: string,
  payload: { feedback?: string; error_reported: boolean }
) => {
  return maiaApi.post(
    `/api/v1/chats/${chatId}/messages/${messageId}/feedback`,
    payload
  )
}

const updateChat = (
  chatId: string | number,
  payload: {
    incognito?: boolean
    title?: string
    personality?: 'neutral' | 'encouraging' | 'reassuring'
  }
) => {
  return maiaApi
    .put<{
      id: string
      title: string
      user_id: number
      incognito: boolean
      personality: 'neutral' | 'encouraging' | 'reassuring'
      created_at: string
    }>(`/api/v1/chats/${chatId}/`, payload)
    .then((response) => response.data)
}

const deleteChat = (chatId: string | number) => {
  return maiaApi.delete(`/api/v1/chats/${chatId}/`)
}

const transcribeAudio = (audioFile: File) => {
  const formData = new FormData()
  formData.append('audio', audioFile)

  return maiaApi
    .post<{ text: string }>('/api/v1/audio/transcribe/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((response) => response.data)
}

const getTasks = () => {
  return maiaApi
    .get<
      Array<{
        id: string
        user_id?: number
        created_by_id?: number
        task: string
        description?: string
        status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
        priority?: 'low' | 'medium' | 'high'
        deadline: string | null
        type?: string
      }>
    >('/api/v1/tasks/')
    .then((response) => response.data)
}

const updateTask = (
  taskId: string,
  payload: {
    task?: string
    type?: string
    description?: string
    status?: string
    deadline?: string | null
    archived?: boolean
  }
) => {
  return maiaApi
    .put<{
      id: string
      user_id: number
      created_by_id?: number
      task: string
      type?: string
      description?: string
      status: string
      deadline: string | null
      created_at: string
      updated_at: string
      archived: boolean
    }>(`/api/v1/tasks/${taskId}/`, payload)
    .then((response) => response.data)
}

const createTask = (
  userId: string | number | null,
  payload: {
    task: string
    type?: string
    description?: string
    status?: string
    deadline?: string | null
  }
) => {
  const params = userId ? { user_id: userId } : {}
  return maiaApi
    .post<{
      id: string
      user_id: number
      created_by_id: number
      task: string
      type: string
      description: string
      status: string
      deadline: string | null
      created_at: string
      updated_at: string
      archived: boolean
    }>('/api/v1/tasks/', payload, { params })
    .then((response) => response.data)
}

// Types pour user-memory
type UserMemoryResponse = {
  user_role: string
  user_data?: {
    user_id: number
    profile?: {
      points_forts?: string[]
      axes_amelioration?: string[]
      prochaines_etapes?: string[]
    }
  }
  jeunes: Array<{
    user_id: number
    firstname: string
    lastname: string
    email?: string
    profile?: {
      points_forts?: string[]
      axes_amelioration?: string[]
      prochaines_etapes?: string[]
    }
  }>
}

const getUserMemory = () => {
  return maiaApi
    .get<UserMemoryResponse>('/api/v1/chats/user-memory')
    .then((response) => response.data)
}

const getMenteeTasks = (userId: string | number) => {
  return maiaApi
    .get<
      Array<{
        id: string
        user_id?: number
        created_by_id?: number
        task: string
        type?: string
        description?: string
        status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
        deadline: string | null
        priority?: 'low' | 'medium' | 'high'
      }>
    >(`/api/v1/tasks/`, {
      params: { user_id: userId },
    })
    .then((response) => response.data)
}

const getRecommendations = (
  jeuneId?: number | string,
  includeArchived = false
) => {
  return maiaApi
    .get<Recommendation[]>('/api/v1/recommendations/', {
      params: {
        jeune_id: jeuneId,
        include_archived: includeArchived,
      },
    })
    .then((response) => response.data)
}

const getRecommendation = (recommendationId: string) => {
  return maiaApi
    .get<Recommendation>(`/api/v1/recommendations/${recommendationId}/`)
    .then((response) => response.data)
}

const updateRecommendation = (
  recommendationId: string,
  payload: {
    status?: string
    archived?: boolean
  }
) => {
  return maiaApi
    .put<Recommendation>(
      `/api/v1/recommendations/${recommendationId}/`,
      payload
    )
    .then((response) => response.data)
}

const deleteRecommendation = (recommendationId: string) => {
  return maiaApi
    .delete(`/api/v1/recommendations/${recommendationId}/`)
    .then((response) => response.data)
}

const ChatbotService = {
  createNewConversation,
  createTask,
  deleteChat,
  deleteRecommendation,
  getChatMessages,
  getConversations,
  getMenteeTasks,
  getRecommendation,
  getRecommendations,
  getUserMemory,
  getTasks,
  sendMessageFeedback,
  transcribeAudio,
  updateChat,
  updateRecommendation,
  updateTask,
}

export default ChatbotService
